import type { Action } from 'state/actions';
import * as addonActionTypes from 'state/addonForm/addonForm.actionTypes';
import * as addonManagementActionTypes from 'state/addonManagementForm/addonManagementForm.actionTypes';
import * as batchActionTypes from 'state/batchForm/batchForm.actionTypes';
import * as dynamicActionTypes from 'state/dynamic/dynamicForm.actionTypes';
import { addKey, addOrPatchKey, patchKey, patchKeyIfExist, removeKey, removeKeys } from 'state/utils/collection';
import { dateNow } from 'utils/core/now';

import { isQuoteTypeTradable } from '../utils';
import * as tradesActionTypes from './trades.actionTypes';
import type { NextTradeAuthorizationStatus, TradeInfos, TradeInfosMap, TradeStatus } from './trades.model';
import { initialTradeInfos } from './trades.model';

export function tradesReducer(state: TradeInfosMap = {}, action: Action): TradeInfosMap {
  switch (action.type) {
    case 'QUOTE_ACKED':
    case addonActionTypes.ADDON_QUOTE_ACKED:
      if (isQuoteTypeTradable(action.quoteType)) {
        return addKey(state, action.quoteId, initialTradeInfos);
      }
      return state;

    case dynamicActionTypes.DYNAMIC_BOOKING_REQUESTED:
      return addKey(state, action.quoteId, initialTradeInfos);

    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_ACKED:
      return action.forms.reduce((result, form) => addKey(result, form.quoteId, initialTradeInfos), state);
    case addonActionTypes.ADDON_QUOTE_RECEIVED:
      return patchKey(state, action.quoteId, () => ({
        tradeAuthDurationInMillis: action.timeToTradeInMs,
        tradeAuthStartTimeInMillis: dateNow(),
      }));

    case dynamicActionTypes.DYNAMIC_BOOKING_QUOTE_FAILED:
    case addonActionTypes.ADDON_QUOTE_REMOVED:
      return removeKey(state, action.quoteId);
    case 'QUOTES_REMOVED':
      return removeKeys(state, action.quoteIds);
    case 'STREAMING_TRADE_AUTH_RECEIVED':
      return addKey(state, action.quoteId, {
        ...initialTradeInfos,
        tradeAuthStatus: 'Granted' satisfies NextTradeAuthorizationStatus,
      } as TradeInfos);
    case 'TRADE_AUTH_RECEIVED':
    case batchActionTypes.BATCH_TRADE_AUTH_RECEIVED: {
      return addOrPatchKey(state, action.quoteId, () => ({
        tradeAuthStatus: action.status,
        tradeAuthStartTimeInMillis: action.when.getTime(),
        tradeAuthDurationInMillis: action.expirationDelayInSeconds * 1000,
        tradeAuthRejections: action.rejections,
      }));
    }
    case 'TRADE_AUTH_TIMEOUT':
      return addOrPatchKey(state, action.quoteId, () => ({
        tradeAuthStatus: 'Timeout' as const,
        tradeAuthStartTimeInMillis: undefined,
        tradeAuthDurationInMillis: undefined,
      }));
    case 'QUOTE_TRADE_SENT':
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_SENT:
    case addonActionTypes.ADDON_QUOTE_TRADE_SENT:
      return addOrPatchKey(state, action.quoteId, () => ({
        tradeStatus: 'Pending' as TradeStatus,
      }));
    case addonActionTypes.ADDON_QUOTE_TRADE_REQUESTED:
      return state;
    case 'QUOTE_TRADE_REJECTED':
    case dynamicActionTypes.DYNAMIC_BOOKING_TRADE_REJECTED:
    case addonActionTypes.ADDON_QUOTE_TRADE_REJECTED:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REJECTED:
      return addOrPatchKey(state, action.quoteId, () => ({
        tradeStatus: 'Rejected' as TradeStatus,
        tradeRejections: action.rejections,
      }));
    case 'QUOTE_TRADE_FAILED':
    case addonActionTypes.ADDON_QUOTE_TRADE_FAILED:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_FAILED:
      return patchKey(state, action.quoteId, () => ({
        tradeStatus: 'Failed' as TradeStatus,
        technicalError: action.error,
      }));
    case 'QUOTE_TRADE_DONE':
    case 'QUOTE_STREAMING_TRADE_DONE':
      return patchKey(state, action.quoteId, () => ({
        tradeStatus: 'Done' as TradeStatus,
        bookingSkipped: action.bookingSkipped ?? false,
      }));
    case addonActionTypes.ADDON_QUOTE_TRADE_DONE:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_DONE:
      return patchKey(state, action.quoteId, () => ({
        tradeStatus: 'Done' as TradeStatus,
        bookingSkipped: false,
        execMessages: action.execMessages,
      }));

    case tradesActionTypes.TRADE_EXECUTION_COMMENT_CHANGED:
      return patchKey(state, action.quoteId, () => ({
        comment: action.comment,
      }));

    case tradesActionTypes.TRADE_EXECUTION_NOTIFICATIONS_CHANGED:
      return patchKey(state, action.quoteId, () => ({
        tradeNotifications: action.contactIds,
      }));

    case tradesActionTypes.TRADES_CONFIRMED_EXECUTION_ONLY_DEAL_ID:
      return patchKey(state, action.quoteId, () => ({
        executionOnlyDealId: action.dealId,
      }));

    case dynamicActionTypes.DYNAMIC_BOOKING_TRADE_DONE:
      return patchKeyIfExist(state, action.quoteId, () => ({
        tradeStatus: 'Done' as TradeStatus,
        bookingSkipped: false,
      }));

    default:
      return state;
  }
}
